import {useRef, useState} from "react";
import {Button, Form, Space, Table, Tag, Typography} from "antd";
import {t} from "i18next";
import ApiService from "../../../core/service/api-service";
import AckRuleEditor from "./AckRuleEditor";
import {serializeRules} from "./ackRuleUtils";

// messageIds: HL7 report-message ids whose stored ACK will be tested.
// rowsById: optional map id -> row (for an accession fallback in the result).
// onTested: called after a test run, since the test also rewrites the report statuses.
export default function AckRuleTestModal({messageIds = [], rowsById = {}, onTested}) {
    const formRef = useRef();
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);

    const runTest = async () => {
        const values = formRef.current.getFieldsValue();
        const rules = serializeRules(values.ackRuleItems);

        setLoading(true);
        const res = await ApiService.HL7Service.TestAckRules(rules, messageIds);
        setLoading(false);

        if (res.success) {
            setResults(res.data || []);
            onTested?.();
        }
    };

    const successCount = results ? results.filter(r => r.success).length : 0;

    return (
        <div>
            <Form ref={formRef} layout={'vertical'}>
                <AckRuleEditor name={'ackRuleItems'}/>
            </Form>

            <Space style={{marginTop: 12}}>
                <Button type={'primary'} loading={loading} onClick={runTest} disabled={messageIds.length === 0}>
                    {t('Test')}
                </Button>
                <Typography.Text type={'secondary'}>
                    {messageIds.length} {t('messages')} — {t('Report statuses are updated with the test result')}
                </Typography.Text>
            </Space>

            {results && (
                <>
                    <Typography.Text strong style={{display: 'block', marginTop: 16}}>
                        {t('Success')}: {successCount} / {results.length}
                    </Typography.Text>
                    <Table
                        style={{marginTop: 8}}
                        size={'small'}
                        rowKey={'id'}
                        pagination={false}
                        dataSource={results}
                        columns={[
                            {
                                title: t('Accession Number'),
                                dataIndex: 'accessionNumber',
                                width: 160,
                                render: (v, r) => v || rowsById?.[r.id]?.accessionNumber || r.id
                            },
                            {
                                title: t('Result'),
                                dataIndex: 'success',
                                width: 110,
                                render: (v) => v
                                    ? <Tag color={'green'}>{t('Success')}</Tag>
                                    : <Tag color={'red'}>{t('Failed')}</Tag>
                            },
                            {
                                title: 'ACK',
                                dataIndex: 'ack',
                                render: (v, r) => r.hasAck
                                    ? (
                                        <Typography.Text
                                            style={{maxWidth: 420, display: 'inline-block'}}
                                            ellipsis={{tooltip: v}}
                                        >
                                            {v}
                                        </Typography.Text>
                                    )
                                    : <Typography.Text type={'secondary'}>{t('No ACK')}</Typography.Text>
                            }
                        ]}
                    />
                </>
            )}
        </div>
    );
}
